'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { TOCHeading } from './TableOfContents'

/** How long a clicked heading stays pinned. Covers a smooth scroll settling. */
const PIN_DURATION_MS = 1000

/**
 * Tracks which heading is currently visible using IntersectionObserver, and returns `pin` so a
 * clicked table-of-contents link can claim the highlight immediately.
 *
 * `pin` exists because the observer alone cannot be trusted the instant a link is clicked: it
 * ignores the top 80px of the viewport, so a heading scrolled to the top of the page is briefly
 * outside the detection band and the next heading down would claim the highlight instead. Headings
 * carry `scroll-mt-24` so they land inside the band, and pinning makes the match immediate and
 * deterministic rather than dependent on that geometry.
 */
export function useActiveHeading(headings: TOCHeading[]): {
  activeId: string
  pin: (id: string) => void
} {
  const [activeId, setActiveId] = useState<string>('')
  const pinnedUntil = useRef(0)

  useEffect(() => {
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < pinnedUntil.current) return

        const intersecting = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

        if (intersecting.length > 0) {
          setActiveId(intersecting[0].target.id)
        }
      },
      {
        // 80px top inset keeps a heading level with the very top of the viewport from counting as
        // "being read". -60% bottom inset means only the top 40% of the viewport activates a
        // heading, so the highlight tracks what the reader is looking at.
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0,
      },
    )

    headings.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [headings])

  const pin = useCallback((id: string) => {
    pinnedUntil.current = Date.now() + PIN_DURATION_MS
    setActiveId(id)
  }, [])

  return { activeId, pin }
}
